import React from 'react';
import { Calendar, Clock, Edit, CalendarClock, Download, Flag, Plus, ArrowUp, ArrowDown, Info } from 'lucide-react';
import { Button } from '../ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const formatDueDate = (dateStr) => {
  if (!dateStr) return 'Not set';
  const parts = dateStr.split('-');
  if (parts.length !== 3) return dateStr;
  return `${parseInt(parts[1])}-${parseInt(parts[2])}-${parts[0]}`;
};

export const ProgressTracker = ({ tasks, taskStats, downloadPDF }) => (
  <div className="bg-gradient-to-r from-blue-50 to-blue-100 border border-blue-200 rounded-xl p-4 sm:p-6 mb-6">
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
      <h2 className="text-lg sm:text-xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
        Your Progress
      </h2>
      <div className="flex items-center gap-2 sm:gap-4">
        <Button
          onClick={downloadPDF}
          variant="outline"
          size="sm"
          className="bg-white hover:bg-slate-50"
          data-testid="download-pdf-button"
        >
          <Download className="w-4 h-4 sm:mr-2" />
          <span className="hidden sm:inline">Download PDF</span>
        </Button>
        <div className="text-right">
          <div className="text-3xl font-bold text-blue-600">
            {tasks.length > 0 ? Math.round((tasks.filter(t => t.status === 'complete').length / tasks.length) * 100) : 0}%
          </div>
          <div className="text-xs text-slate-600">Complete</div>
        </div>
      </div>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
      <div className="bg-white rounded-lg p-4 border border-blue-200">
        <div className="text-sm text-slate-600 mb-1">Total Tasks</div>
        <div className="text-2xl font-bold text-slate-900">{tasks.length}</div>
      </div>
      <div className="bg-white rounded-lg p-4 border border-blue-200">
        <div className="text-sm text-slate-600 mb-1">Completed</div>
        <div className="text-2xl font-bold text-green-600">{taskStats.completedCount}</div>
      </div>
      <div className="bg-white rounded-lg p-4 border border-blue-200">
        <div className="text-sm text-slate-600 mb-1">Hours Logged</div>
        <div className="text-2xl font-bold text-blue-600">{taskStats.totalHours.toFixed(1)}h</div>
      </div>
    </div>
    <div className="w-full bg-white rounded-full h-3 border border-blue-200">
      <div
        className="bg-gradient-to-r from-blue-500 to-blue-600 h-3 rounded-full transition-all duration-500"
        style={{ width: `${taskStats.completionPct}%` }}
      ></div>
    </div>
    <div className="text-xs text-slate-600 mt-2 text-center">{taskStats.openCount} tasks remaining</div>
  </div>
);

export const MilestonesSection = ({ milestones, planInfos }) => {
  if (milestones.length === 0) return null;

  const planLookup = {};
  (planInfos || []).forEach(p => { planLookup[p.plan_id] = p.plan_name; });

  const showPerPlan = (planInfos || []).length > 1;

  if (!showPerPlan) {
    const planName = (planInfos || []).length === 1 ? planInfos[0].plan_name : null;
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-4 sm:p-6 mb-6" data-testid="milestones-section">
        <div className="flex items-center gap-2 mb-4">
          <Flag className="w-5 h-5 text-blue-600" />
          <h2 className="text-lg sm:text-xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Milestones
          </h2>
          {planName && <span className="text-sm text-slate-500 ml-2">({planName})</span>}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {milestones.map((ms, idx) => (
            <div key={ms.id} className="flex items-start gap-3 border border-blue-100 bg-blue-50/50 rounded-lg p-3" data-testid={`user-milestone-${ms.id}`}>
              <div className="flex items-center justify-center w-7 h-7 rounded-full bg-blue-600 text-white font-bold text-xs shrink-0 mt-0.5">
                {ms.order || idx + 1}
              </div>
              <div className="min-w-0">
                <h4 className="font-semibold text-slate-900 text-sm">{ms.title}</h4>
                {ms.description && <p className="text-xs text-slate-500 mt-0.5">{ms.description}</p>}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Group milestones by plan
  const grouped = {};
  milestones.forEach(ms => {
    const pid = ms.study_plan_template_id || 'unknown';
    if (!grouped[pid]) grouped[pid] = [];
    grouped[pid].push(ms);
  });

  return (
    <>
      {Object.entries(grouped).map(([planId, planMilestones]) => (
        <div key={planId} className="bg-white border border-slate-200 rounded-xl p-4 sm:p-6 mb-6" data-testid={`milestones-section-${planId}`}>
          <div className="flex items-center gap-2 mb-4">
            <Flag className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg sm:text-xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Milestones
            </h2>
            <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 border border-blue-200">
              {planLookup[planId] || 'Study Plan'}
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {planMilestones.map((ms, idx) => (
              <div key={ms.id} className="flex items-start gap-3 border border-blue-100 bg-blue-50/50 rounded-lg p-3" data-testid={`user-milestone-${ms.id}`}>
                <div className="flex items-center justify-center w-7 h-7 rounded-full bg-blue-600 text-white font-bold text-xs shrink-0 mt-0.5">
                  {ms.order || idx + 1}
                </div>
                <div className="min-w-0">
                  <h4 className="font-semibold text-slate-900 text-sm">{ms.title}</h4>
                  {ms.description && <p className="text-xs text-slate-500 mt-0.5">{ms.description}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </>
  );
};

// Parse date string as local time (avoids UTC timezone offset shifting dates by -1 day)
const parseLocalDate = (dateStr) => {
  if (!dateStr) return null;
  const parts = dateStr.split('-');
  if (parts.length === 3) return new Date(parts[0], parts[1] - 1, parts[2]);
  return new Date(dateStr + 'T00:00:00');
};

export const StartDateSection = ({ currentStartDate, studyPlanName, expirationDate, openStartDateDialog }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-4 sm:p-6 mb-6">
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
      <div className="flex items-center gap-2 flex-wrap">
        <Calendar className="w-5 h-5 text-blue-600 shrink-0" />
        <h2 className="text-lg sm:text-xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Study Plan Start Date
        </h2>
        {studyPlanName && (
          <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 border border-blue-200">
            {studyPlanName}
          </span>
        )}
      </div>
      {currentStartDate && (
        <span className="text-sm text-slate-600">
          Start: <strong>{parseLocalDate(currentStartDate).toLocaleDateString()}</strong>
        </span>
      )}
    </div>
    <div className="text-sm text-slate-600 mb-1" data-testid="expiration-date-display">
      <span className="font-medium">Expiration Date:</span>{' '}
      {expirationDate
        ? <strong>{parseLocalDate(expirationDate).toLocaleDateString()}</strong>
        : <span className="italic text-slate-500">No expiration date is set.</span>}
    </div>
    <p className="text-sm text-slate-600 mb-4" style={{ fontFamily: 'Figtree, sans-serif' }}>
      {currentStartDate
        ? 'Your study plan dates are calculated from this start date. Click Edit to change. Please note, completion dates are not automatically updated via your course. You should edit each task status as Open or Complete.'
        : 'Set your start date to automatically calculate all task due dates based on the formula.'}
    </p>
    <Button onClick={openStartDateDialog} className="bg-blue-600 hover:bg-blue-700" data-testid="edit-start-date-button">
      <Edit className="w-4 h-4 mr-2" />
      {currentStartDate ? 'Edit Start Date' : 'Set Start Date'}
    </Button>
  </div>
);

export const TaskListView = ({ sortedTasks, filter, setFilter, sortBy, sortOrder, toggleSort, handleEditTask }) => (
  <>
    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
      <Select value={filter} onValueChange={setFilter}>
        <SelectTrigger className="w-full sm:w-40" data-testid="status-filter">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Tasks</SelectItem>
          <SelectItem value="open">Open</SelectItem>
          <SelectItem value="complete">Complete</SelectItem>
        </SelectContent>
      </Select>
      <div className="flex items-center gap-2">
        <span className="text-sm text-slate-600">Sort by:</span>
        {[
          { key: 'due_date', label: 'Due Date' },
          { key: 'activity', label: 'Name' },
          { key: 'status', label: 'Status' },
        ].map(({ key, label }) => (
          <Button
            key={key}
            variant={sortBy === key ? 'default' : 'outline'}
            size="sm"
            onClick={() => toggleSort(key)}
            className="gap-1"
            data-testid={`sort-by-${key === 'activity' ? 'activity' : key === 'due_date' ? 'date' : key}`}
          >
            {label}
            {sortBy === key && (sortOrder === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)}
          </Button>
        ))}
      </div>
    </div>

    {sortedTasks.length === 0 ? (
      <div className="text-center py-12" data-testid="no-tasks-message">
        <p className="text-slate-500" style={{ fontFamily: 'Figtree, sans-serif' }}>No tasks found. Contact your admin to add tasks.</p>
      </div>
    ) : (
      <>
        {/* Desktop table */}
        <div className="hidden sm:block overflow-x-auto overflow-y-visible">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Activity</TableHead>
                <TableHead>Content</TableHead>
                <TableHead>Goals</TableHead>
                <TableHead className="whitespace-nowrap">Due Date</TableHead>
                <TableHead>
                  <span className="flex items-center gap-1">
                    Hours Log
                    <span className="relative group/tip">
                      <Info className="w-3.5 h-3.5 text-blue-500 cursor-help" />
                      <span className="absolute top-full left-1/2 -translate-x-1/2 mt-1.5 hidden group-hover/tip:block bg-slate-800 text-white text-xs rounded-md px-3 py-2 whitespace-nowrap z-[9999] shadow-lg pointer-events-none">
                        Enter time as .25, .50, .75, or whole hours
                      </span>
                    </span>
                  </span>
                </TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedTasks.map((task) => (
                <TableRow key={task.id} data-testid={`task-row-${task.id}`}>
                  <TableCell className="font-medium">{task.activity}</TableCell>
                  <TableCell className="w-[25%] whitespace-normal break-words text-sm">{task.content}</TableCell>
                  <TableCell className="w-[25%] whitespace-normal break-words">{task.goals}</TableCell>
                  <TableCell className="whitespace-nowrap">{formatDueDate(task.due_date)}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Clock className="w-4 h-4 text-slate-400" />
                      {task.hours_log} hrs
                    </div>
                  </TableCell>
                  <TableCell>
                    <span
                      className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                        task.status === 'open'
                          ? 'bg-blue-100 text-blue-800 border border-blue-200'
                          : 'bg-slate-100 text-slate-800 border border-slate-200'
                      }`}
                      data-testid={`task-status-${task.id}`}
                    >
                      {task.status}
                    </span>
                  </TableCell>
                  <TableCell>
                    <Button size="sm" variant="outline" onClick={() => handleEditTask(task)} data-testid={`edit-task-button-${task.id}`}>
                      Edit
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Mobile card view */}
        <div className="sm:hidden space-y-3">
          {sortedTasks.map((task) => (
            <div key={task.id} className="border border-slate-200 rounded-lg p-3" data-testid={`task-card-${task.id}`}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <h4 className="font-semibold text-slate-900 text-sm">{task.activity}</h4>
                <span
                  className={`px-2 py-0.5 rounded-full text-xs font-semibold shrink-0 ${
                    task.status === 'open'
                      ? 'bg-blue-100 text-blue-800 border border-blue-200'
                      : 'bg-green-100 text-green-800 border border-green-200'
                  }`}
                  data-testid={`task-status-${task.id}`}
                >
                  {task.status}
                </span>
              </div>
              {task.content && <p className="text-xs text-slate-600 mb-2 line-clamp-2">{task.content}</p>}
              <div className="space-y-1.5 text-xs text-slate-600">
                {task.goals && (
                  <div className="flex gap-2">
                    <span className="font-medium text-slate-500 shrink-0">Goals:</span>
                    <span className="line-clamp-2">{task.goals}</span>
                  </div>
                )}
                <div className="flex gap-2">
                  <span className="font-medium text-slate-500 shrink-0">Due:</span>
                  <span>{formatDueDate(task.due_date)}</span>
                </div>
                <div className="flex gap-2">
                  <span className="font-medium text-slate-500 shrink-0">Hours:</span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3 text-slate-400" />
                    {task.hours_log} hrs
                  </span>
                </div>
              </div>
              <div className="flex gap-2 mt-3 pt-2 border-t border-slate-100">
                <Button size="sm" variant="outline" className="flex-1 text-xs h-8" onClick={() => handleEditTask(task)} data-testid={`edit-task-button-${task.id}`}>
                  <Edit className="w-3 h-3 mr-1" />
                  Edit
                </Button>
              </div>
            </div>
          ))}
        </div>
      </>
    )}
  </>
);

export const ExpirationWarning = ({ expirationInfo }) => {
  if (!expirationInfo.showWarning) return null;
  return (
    <div
      className={`rounded-xl p-4 mb-6 border ${
        expirationInfo.isUrgent ? 'bg-red-50 border-red-200' : 'bg-yellow-50 border-yellow-200'
      }`}
      data-testid="expiration-warning"
    >
      <div className="flex items-center gap-3">
        <CalendarClock className={`w-5 h-5 shrink-0 ${expirationInfo.isUrgent ? 'text-red-600' : 'text-yellow-600'}`} />
        <p className={`text-sm font-medium ${expirationInfo.isUrgent ? 'text-red-800' : 'text-yellow-800'}`}>
          {expirationInfo.isUrgent
            ? `Your access expires in ${expirationInfo.daysLeft} day${expirationInfo.daysLeft !== 1 ? 's' : ''}! `
            : `Your access expires on ${expirationInfo.expirationDate} (${expirationInfo.daysLeft} days remaining). `}
          Contact{' '}
          <a href="mailto:service@omnipear.com" className="underline font-semibold">service@omnipear.com</a>
          {' '}for renewals.
        </p>
      </div>
    </div>
  );
};

export const SecurityQuestionBanner = ({ user, onSetup }) => {
  if (!user || user.has_security_question) return null;
  return (
    <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6" data-testid="security-question-banner">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <p className="text-sm font-medium text-blue-800">Reminder: Set up a security question to enable password reset.</p>
          <p className="text-xs text-blue-600 mt-0.5">This allows you to reset your password from the login page.</p>
        </div>
        <Button size="sm" variant="outline" className="border-blue-300 text-blue-700 hover:bg-blue-100 shrink-0" onClick={onSetup} data-testid="setup-security-question-button">
          Set Up Now
        </Button>
      </div>
    </div>
  );
};

export const ExpiredScreen = ({ CalendarClockIcon }) => (
  <main className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto pb-14">
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="bg-white border border-red-200 rounded-xl p-8 sm:p-12 max-w-lg text-center shadow-sm" data-testid="expired-message">
        <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
          <CalendarClockIcon className="w-8 h-8 text-red-600" />
        </div>
        <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Access Expired
        </h2>
        <p className="text-slate-600 leading-relaxed">
          Your StudyPlan Pro access has expired which is linked to your course access. For reinstatement or course renewals, please contact the OmniPear service team at{' '}
          <a href="mailto:service@omnipear.com" className="text-blue-600 hover:text-blue-700 font-medium underline">service@omnipear.com</a>.
        </p>
      </div>
    </div>
  </main>
);
